import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import tailwindClassnames from "../common/TailwindClassnames";
import SelectInput from "../common/SelectInput";
import { useProfile } from "../common/ProfileProvider";
import AiMappingsLabelTable from "./AiMappingsLabelTable";
import ModelEnablementTable from "./ModelEnablementTable";
import {
  ALL_MODELS_FILTER,
  mergeMappingAndSpeciesRows,
  ModelFilter,
  resolveMappingsModelId,
  resolvePersistedModelFilter,
} from "./aiMappingsHelpers";
import {
  getEnabledModelsForProject,
  getProjectMappings,
  getSpeciesList,
} from "../../api/projectsApi";
import {
  EffectiveMappingItem,
  EffectiveSpeciesListItem,
  EnabledModelItem,
} from "../common/ObjectTypes";

type ManageProjectAiMappingsProps = {
  projectId: number;
  perms: any[];
};

type ModelFilterOption = { value: ModelFilter; label: string };

/**
 * Project-admin AI configuration tab (AKL-679, combined view since AKL-729,
 * inline enable/disable table as a follow-up): a `ModelEnablementTable` lists
 * every globally-active model plus any this project has enabled, with an
 * inline Enable/Disable action per row (disabling asks for confirmation —
 * reversible, re-running the model or re-enabling it restores it). Below
 * that, the AI Mappings table shows every enabled model's mappings combined
 * by default, one row per effective mapping with a Model column; a separate
 * "Filter by model" dropdown narrows to a single model, exactly like the
 * original single-model view, when one is picked instead of "All models".
 * The enabled-models list is project-scoped (`getEnabledModelsForProject`),
 * not the global model registry. Every label falls back to its model's live
 * global default until the project adds its own override. Reads render for
 * anyone who can see the tab; every mutating control is gated on `canManage`
 * (MANAGE_AI_MAPPINGS or is_admin).
 * @param projectId numeric project id
 * @param perms the project permission list from GET /projects/{id}/permissions
 */
const ManageProjectAiMappings: React.FC<ManageProjectAiMappingsProps> = ({
  projectId,
  perms,
}) => {
  const { t } = useTranslation();
  const { profile, profileLoading } = useProfile();

  const storageKey = `aiMappingsSelectedModel_${projectId}`;

  const [canManage, setCanManage] = useState(false);
  const [models, setModels] = useState<EnabledModelItem[]>([]);
  const [modelsLoading, setModelsLoading] = useState(true);
  const [modelFilter, setModelFilter] =
    useState<ModelFilter>(ALL_MODELS_FILTER);

  const [mappings, setMappings] = useState<EffectiveMappingItem[]>([]);
  const [mappingsLoading, setMappingsLoading] = useState(true);

  const [speciesItems, setSpeciesItems] = useState<EffectiveSpeciesListItem[]>(
    [],
  );
  const [speciesLoading, setSpeciesLoading] = useState(true);

  // Derive write permission once the profile has loaded.
  useEffect(() => {
    if (!profileLoading && profile) {
      const isAdmin = profile?.is_admin;
      const hasManage = perms.some(
        (perm: any) => perm.name === "MANAGE_AI_MAPPINGS",
      );
      setCanManage(!!isAdmin || hasManage);
    }
  }, [perms, profile, profileLoading]);

  // Load the project's enabled models once. A previously-persisted specific
  // model_id that's no longer enabled falls back to "All models" silently —
  // no error toast (AKL-729 stale-selection fallback).
  useEffect(() => {
    let cancelled = false;
    const loadModels = async () => {
      setModelsLoading(true);
      try {
        const resp = await getEnabledModelsForProject(projectId);
        if (cancelled) return;
        setModels(resp.models);
        const stored = localStorage.getItem(storageKey);
        setModelFilter(resolvePersistedModelFilter(stored, resp.models));
      } catch (error) {
        console.error("Error loading enabled models:", error);
        if (!cancelled) setModels([]);
      } finally {
        if (!cancelled) setModelsLoading(false);
      }
    };
    loadModels();
    return () => {
      cancelled = true;
    };
  }, [projectId, storageKey]);

  const mappingsFetchId = React.useRef(0);

  const fetchMappings = useCallback(async () => {
    const fetchId = ++mappingsFetchId.current;

    // Wait for the enabled-models list before deciding what (if anything) to
    // fetch — avoids an extra request racing ahead of the stale-selection
    // fallback above.
    if (modelsLoading) return;

    if (models.length === 0) {
      setMappings([]);
      if (fetchId === mappingsFetchId.current) setMappingsLoading(false);
      return;
    }

    setMappingsLoading(true);
    try {
      const resp = await getProjectMappings(
        projectId,
        resolveMappingsModelId(modelFilter),
      );
      if (fetchId !== mappingsFetchId.current) return;
      setMappings(resp.mappings);
    } catch (error) {
      if (fetchId !== mappingsFetchId.current) return;
      // Clear stale rows so actions never target a stale filter's context.
      setMappings([]);
      console.error("Error loading project mappings:", error);
    } finally {
      if (fetchId === mappingsFetchId.current) setMappingsLoading(false);
    }
  }, [projectId, modelFilter, modelsLoading, models.length]);

  const fetchSpecies = useCallback(async () => {
    setSpeciesLoading(true);
    try {
      const resp = await getSpeciesList(projectId);
      setSpeciesItems(resp.items);
    } catch (error) {
      setSpeciesItems([]);
      console.error("Error loading species list:", error);
    } finally {
      setSpeciesLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchMappings();
  }, [fetchMappings]);

  useEffect(() => {
    fetchSpecies();
  }, [fetchSpecies]);

  const handleSelectFilter = (option: ModelFilterOption | null) => {
    const value = option ? option.value : ALL_MODELS_FILTER;
    // Show the loading state immediately so the previous filter's rows don't
    // flash before the new fetch resolves.
    setMappingsLoading(true);
    setModelFilter(value);
    localStorage.setItem(storageKey, String(value));
  };

  // Enable, disable, and re-enable all resolve to "upsert this model_id's
  // entry" — an enable of a never-before-enabled model appends, which alone
  // already re-triggers fetchMappings via its models.length dependency (an
  // explicit call here too would be a redundant second request); a disable
  // or re-enable of an already-enabled one replaces its entry in place
  // instead, which doesn't change models.length, so THAT case needs the
  // explicit refetch below — otherwise the combined "All models" view would
  // keep showing a just-disabled model's rows until some unrelated refetch
  // happened to occur.
  const handleModelUpdated = useCallback(
    (model: EnabledModelItem) => {
      // Computed from `models` (read, not mutated) before calling setModels,
      // not inside the updater — an updater must stay a pure function of its
      // `prev` argument, since React can invoke it more than once (e.g.
      // StrictMode's double-invoke check).
      const wasAlreadyPresent = models.some(
        (m) => m.model_id === model.model_id,
      );
      setModels((prev) => {
        const index = prev.findIndex((m) => m.model_id === model.model_id);
        if (index === -1) return [...prev, model];
        const next = [...prev];
        next[index] = model;
        return next;
      });
      if (wasAlreadyPresent) fetchMappings();
    },
    [models, fetchMappings],
  );

  // A mapping change can alter the effective species list, so refetch both.
  const handleMappingsChanged = useCallback(() => {
    fetchMappings();
    fetchSpecies();
  }, [fetchMappings, fetchSpecies]);

  const modelFilterOptions: ModelFilterOption[] = useMemo(
    () => [
      { value: ALL_MODELS_FILTER, label: t("All models") },
      ...models.map((m) => {
        // A project-disabled model can still be selected here (it stays
        // reachable for direct viewing/editing — see aiMappingsHelpers'
        // model-enablement docs) but its rows won't be in the combined "All
        // models" view, so flag it the same way an inactive model is.
        const suffixes = [
          m.status !== "active" ? t("Inactive") : null,
          m.disabled_at != null ? t("Disabled") : null,
        ].filter((s): s is string => s != null);
        return {
          value: m.model_id,
          label:
            suffixes.length > 0 ? `${m.name} (${suffixes.join(", ")})` : m.name,
        };
      }),
    ],
    [models, t],
  );
  const selectedFilterOption =
    modelFilterOptions.find((o) => o.value === modelFilter) ?? null;

  const modelNamesById = useMemo(() => {
    const map: Record<
      number,
      { name: string; status: string; disabledAt: string | null }
    > = {};
    models.forEach((m) => {
      map[m.model_id] = {
        name: m.name,
        status: m.status,
        disabledAt: m.disabled_at,
      };
    });
    return map;
  }, [models]);

  const mergedRows = useMemo(
    () =>
      mergeMappingAndSpeciesRows(
        mappings,
        speciesItems,
        modelFilter === ALL_MODELS_FILTER,
      ),
    [mappings, speciesItems, modelFilter],
  );

  return (
    <div className="mx-8 pt-6 mb-24">
      <p className={`${tailwindClassnames.helperText} mb-5`}>
        {t(
          "Configure how this project interprets AI predictions. Mappings control which species each model label resolves to (for this project only); the species list is the roster of species available for manual tagging.",
        )}
      </p>
      <div className={tailwindClassnames.formSectionStack}>
        <div className={tailwindClassnames.formSectionCard}>
          <p className={`${tailwindClassnames.helperText} mb-3`}>
            {t(
              "Enabling a model shows its default label mappings here and adds the species they map to your project's species list. Re-point or exclude any label below; changes apply to this project only.",
            )}
          </p>
          {modelsLoading ? (
            <p className={tailwindClassnames.basicLabel}>{t("Loading")}...</p>
          ) : (
            <ModelEnablementTable
              projectId={projectId}
              enabledModels={models}
              canManage={canManage}
              onModelUpdated={handleModelUpdated}
            />
          )}
        </div>

        {!modelsLoading && models.length > 0 && (
          <div className="w-full md:w-1/3">
            <SelectInput
              options={modelFilterOptions}
              className="w-full rounded shadow text-left text-sm"
              isDisabled={false}
              isLoading={false}
              isClearable={false}
              isSearchable={true}
              isMulti={false}
              name={t("Filter by model")}
              value={selectedFilterOption}
              onChange={handleSelectFilter}
              errors={""}
              searchBar={false}
            />
          </div>
        )}

        {!modelsLoading && (
          <AiMappingsLabelTable
            key={String(modelFilter)}
            projectId={projectId}
            modelFilter={modelFilter}
            models={models}
            modelNamesById={modelNamesById}
            rows={mergedRows}
            loading={mappingsLoading || speciesLoading}
            canManage={canManage}
            onChanged={handleMappingsChanged}
          />
        )}
      </div>
    </div>
  );
};

export default ManageProjectAiMappings;
